import { usePlayground } from "../../playground-store";
import { Message as IMessage } from "../../types";

import { usePlaygroundChannel } from "./usePlaygroundChannel";

import { Message } from "@/components/Messaging/types";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";

const useMessageUpdateListener = () => {
  const playground = usePlayground();

  // Helper function to get messageStore from request
  const getMessageStore = (individual_run_request_id: number) => {
    const operation =
      playground.operationStore.getAssociatedOperationByRequestId(individual_run_request_id);
    if (!operation) {
      return null;
    }

    const messageId = playground.operationStore.getAssociatedMessageId(
      operation.id,
      individual_run_request_id,
    );
    if (!messageId) {
      return null;
    }

    const outputInstance = playground.getOutput(operation.output_instance_id!)!;
    const messageStore = outputInstance.getMessage(messageId);

    return { messageStore, outputInstance, messageId };
  };

  // Helper to parse payload and get messageStore
  const parsePayloadAndGetMessageStore = (payload: Message) => {
    const { individual_run_request_id } = JSON.parse(payload.data);
    return getMessageStore(individual_run_request_id);
  };

  // Helper to update tool call status with optional in progress state
  const updateToolCallStatus = (
    result: ReturnType<typeof getMessageStore>,
    status: string | undefined,
    isInProgress?: boolean,
  ) => {
    if (!result?.messageStore || !status) {
      return false;
    }
    if (isInProgress !== undefined) {
      result.messageStore.setIsToolCallInProgress(isInProgress);
    }
    result.messageStore.setToolCallStatus(status);
    return true;
  };

  const handleMessageUpdate = (payload: Message) => {
    const {
      payload: { message },
      individual_run_request_id,
    }: {
      payload: {
        message?: IMessage;
      };
      individual_run_request_id: number;
    } = JSON.parse(payload.data);
    const result = getMessageStore(individual_run_request_id);
    if (!result || !message || !result.messageStore) {
      return;
    }

    const { outputInstance, messageId } = result;

    outputInstance.updateMessage(messageId, message);
  };

  const handleToolSearchStarted = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    if (!result?.messageStore) {
      return;
    }

    result.messageStore.setIsToolCallInProgress(true);
    if (status) {
      result.messageStore.setToolCallStatus(status);
    }
  };

  const handleToolSearchInProgress = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status);
  };

  const handleCodeInterpreterCompleted = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status, false);
  };

  const handleCodeInterpreterInProgress = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status, true);
  };

  const handleCodeInterpreterStarted = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status, true);
  };

  const handleShellStarted = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status, true);
  };

  const handleShellInProgress = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status);
  };

  const handleShellCompleted = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status, false);
  };

  const handleApplyPatchStarted = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status, true);
  };

  const handleApplyPatchInProgress = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status);
  };

  const handleApplyPatchCompleted = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    updateToolCallStatus(result, status, false);
  };

  const handleToolSearchCompleted = (payload: Message) => {
    const {
      payload: { status },
    } = JSON.parse(payload.data);
    const result = parsePayloadAndGetMessageStore(payload);
    if (!result?.messageStore) {
      return;
    }
    result.messageStore.setIsToolCallInProgress(false);
    if (status) {
      result.messageStore.setToolCallStatus(status);
    }
  };

  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.UPDATE_LAST_MESSAGE, handleMessageUpdate);
  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.TOOL_SEARCH_STARTED, handleToolSearchStarted);
  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.TOOL_SEARCH_SEARCHING, handleToolSearchInProgress);
  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.TOOL_SEARCH_COMPLETED, handleToolSearchCompleted);
  usePlaygroundChannel(
    WEBSOCKET_MESSAGE_NAME.CODE_INTERPRETER_COMPLETED,
    handleCodeInterpreterCompleted,
  );
  usePlaygroundChannel(
    WEBSOCKET_MESSAGE_NAME.CODE_INTERPRETER_IN_PROGRESS,
    handleCodeInterpreterInProgress,
  );
  usePlaygroundChannel(
    WEBSOCKET_MESSAGE_NAME.CODE_INTERPRETER_STARTED,
    handleCodeInterpreterStarted,
  );
  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.SHELL_STARTED, handleShellStarted);
  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.SHELL_IN_PROGRESS, handleShellInProgress);
  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.SHELL_COMPLETED, handleShellCompleted);
  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.APPLY_PATCH_STARTED, handleApplyPatchStarted);
  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.APPLY_PATCH_IN_PROGRESS, handleApplyPatchInProgress);
  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.APPLY_PATCH_COMPLETED, handleApplyPatchCompleted);
};

export default useMessageUpdateListener;
